import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { Roles } from "@ucs/Helpers/Enums";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    destination = "destination",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.destination]: LinkFieldValue;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    return {
        billToState: getBillToState(state, cart?.billToId),
        isAuthenticated: state.context.session!.isAuthenticated,
        currentLocation: getLocation(state),
        // isAD: state.context.session!.userRoles.includes(Roles.AD),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasShellContext;

export interface HeaderBillToAddressStyles {
    billToAddressWrapper?: InjectableCss;
    container?: InjectableCss;
    labelTypography?: TypographyPresentationProps;
    companyInfo?: TypographyPresentationProps;
    changeLink?: LinkPresentationProps;
}

export const headerBillToAddressStyles: HeaderBillToAddressStyles = {
    container: {
        css: css`
            display: flex;
            justify-content: flex-start;
         `,
    },
    billToAddressWrapper: {
        css: css`
            display: inline-flex;
            flex-direction: column;
            margin-left: 10px;
            width: 300px;
            padding-bottom: 10px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                        width: 100%;
                        margin-left: 35px;
                    `,
                        css`
                            width: 100%;
                            margin-left: 35px;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
    labelTypography: {
        color: UCSColors.gray04,
        size: 13,
        weight: 700,
        transform: "uppercase",
        css: css`
            letter-spacing: 0.1em;
            line-height: 16px;
            margin-bottom: 4px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 12px;
                    `,
                    css`
                        font-size: 12px;
                    `,
                ]);
            }}
        `,
    },
    companyInfo: {
        size: 18,
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 17px;
                    `,
                    css`
                        font-size: 17px;
                    `,
                ]);
            }}
        `,
    },
    changeLink: {
        color: "primary",
        typographyProps: {
            size: 14,
            css: css`
                margin-top: 8px;
                line-height: 18px;
            `,
        },
    },
};

const HeaderBillToAddress: FC<Props> = ({ id, billToState, isAuthenticated, fields, currentLocation }) => {
    const styles = headerBillToAddressStyles;
    const link = useGetLink(fields.destination || "");

    if (!isAuthenticated) {
        return null;
    }

    return (
        <>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.billToAddressWrapper}>
                    <Typography {...styles.labelTypography} data-test-selector="header_billToLabel">
                        {`${translate("Ordering for")}:`}
                    </Typography>
                    <Typography {...styles.companyInfo} data-test-selector="header_billToLabel">
                        {billToState.value?.customerNumber}
                    </Typography>
                    <Typography {...styles.companyInfo} data-test-selector="header_billToLabel">
                        {billToState.value?.companyName}
                    </Typography>
                    <Typography {...styles.companyInfo} data-test-selector="header_billToLabel">
                        {billToState.value?.fullAddress}
                    </Typography>
                    {link?.url && (
                        <Link
                            href={`${link.url}?returnUrl=${
                                currentLocation.pathname === "/cart" ? currentLocation.pathname : "/my-account"
                            }`}
                            {...styles.changeLink}
                        >
                            {translate("Change")}
                        </Link>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withIsInShell(HeaderBillToAddress)),
    definition: {
        displayName: "Header Bill To Address Menu",
        icon: "MapPin",
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.destination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
